import { useState } from "react";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BUDGETS = [
  "‹ ₹15k",
  "₹25k – ₹35k",
  "₹50k – ₹1L",
  "₹2L+",
];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    budget: "",
    message: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
  e.preventDefault();

  if (!form.name || !form.email || !form.message) {
    toast({
      title: "Missing details",
      description: "Please fill in your name, email and message.",
    });
    return;
  }

  try {
    const response = await fetch("/api/contact.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Something went wrong.");
    }

    toast({
      title: "Message sent – talk soon!",
      description: `Thanks ${form.name.split(" ")[0]}, I'll reply within 24 hours.`,
    });

    setForm({
      name: "",
      email: "",
      budget: "",
      message: "",
    });

  } catch (error) {
    console.error("Contact form error:", error);

    toast({
      title: "Something went wrong",
      description: "We couldn't send your message. Please try again.",
      variant: "destructive",
    });
  }
};

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="bg-neutral-950 text-white rounded-[24px] md:rounded-[32px] p-5 sm:p-8 md:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#FFE14D]/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/[0.03] blur-3xl" />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 relative">
            <div className="md:col-span-5 reveal">
              <p className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-4">
                — Let&apos;s Talk
              </p>
              <h2 className="display text-[36px] sm:text-[42px] md:text-[68px] leading-[0.98]">
                Got an idea? <br />
                Let&apos;s{" "}
                <span className="handwritten text-[#FFE14D]">build</span> it.
              </h2>
              <p className="mt-6 text-neutral-300 text-[15px] leading-relaxed max-w-md">
                Whether it&apos;s a new Shopify store, a WordPress redesign or
                a quick WooCommerce fix drop me a message and I&apos;ll get
                back within 24 hours.
              </p>

              <div className="mt-10 space-y-4">
                <a
                  href="mailto:Khedkarprince77@gmail.com"
                  className="flex items-center gap-3 group"
                >
                  <span className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center group-hover:bg-[#FFE14D] transition-colors">
                    <Mail size={16} />
                  </span>
                  <span className="font-medium">hiremestudioo@gmail.com</span>
                </a>
                <a
                  href="tel:+918225974988"
                  className="flex items-center gap-3 group"
                >
                  <span className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center group-hover:bg-[#FFE14D] transition-colors">
                    <Phone size={16} />
                  </span>
                  <span className="font-medium">+91 8225974988</span>
                </a>
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center">
                    <MapPin size={16} />
                  </span>
                  <span className="font-medium">Working worldwide · IST</span>
                </div>
              </div>
            </div>

            <form
              onSubmit={submit}
              className="md:col-span-7 bg-white text-black rounded-[20px] md:rounded-[24px] p-5 sm:p-6 md:p-10 shadow-xl reveal"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500">
                    Your name
                  </label>
                  <input
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                    className="mt-2 w-full h-12 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@brand.com"
                    className="mt-2 w-full h-12 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="text-xs uppercase tracking-widest text-neutral-500">
                  Budget in INR (optional)
                </label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {BUDGETS.map((b) => (
                    <button
                      type="button"
                      key={b}
                      onClick={() => setForm({ ...form, budget: b })}
                      className={`h-10 px-4 rounded-full text-sm border-2 transition-colors ${
                        form.budget === b
                          ? "bg-black text-white border-black"
                          : "border-black/10 hover:border-black"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="text-xs uppercase tracking-widest text-neutral-500">
                  Tell me about your project
                </label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="I want a Shopify store for my skincare brand..."
                  className="mt-2 w-full border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] resize-none py-2 transition-colors"
                />
              </div>

              <button
                type="submit"
                className="mt-8 h-12 pl-6 pr-2 rounded-full bg-black text-white text-sm font-semibold hover:bg-neutral-800 transition-colors flex items-center gap-2 group"
              >
                Send message
                <span className="w-9 h-9 rounded-full bg-[#FFE14D] text-black flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight size={16} />
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
